import React, { use, useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import dayjs from "dayjs";
import "../../../styles/Auth/employee.css";
import { USER_ROLES } from "../../../utils/constants.js";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Tooltip,
    Switch
} from '@mui/material';
import { Box, Modal, Button, IconButton, Chip } from '@mui/material';
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import plusIcon from '@iconify/icons-mdi/eye-circle-outline';
import editIcon from '@iconify/icons-mdi/square-edit-outline';
import deleteIcon from '@iconify/icons-mdi/trash-can-outline';
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import trayUpload from "@iconify/icons-mdi/tray-upload";

import { postData, uploadFile, getData, putData, deleteData } from "../../../webService/webservice.js";
import StoreMultiSelect from "../Employee/multiStoreSelect.jsx";
import TVFMLoader from "../Loader/loader.jsx";
import Footer from "../Footer/Footer-page.jsx";

const DetailItem = ({ label, value, fullWidth, icon, color = "#1a1e27" }) => (
    <Box sx={{
        gridColumn: fullWidth ? 'span 2' : 'span 1',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        pb: 1.5,
        mb: 0.5,
        borderBottom: '1px solid #f8fafc'
    }}>
        <Box sx={{
            color: color,
            display: 'flex',
            mt: 0.5,
            p: 1.2,
            borderRadius: '12px',
            bgcolor: `${color}15`,
            boxShadow: `0 4px 10px ${color}10`,
            border: `1px solid ${color}20`
        }}>
            {icon && <Icon icon={icon} style={{ fontSize: '18px' }} />}
        </Box>
        <Box sx={{ flex: 1 }}>
            <Typography
                variant="caption"
                sx={{
                    color: '#64748b',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '10px',
                    display: 'block',
                    mb: 0.2
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: '#1e293b',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                    lineHeight: 1.4
                }}
            >
                {value || "-"}
            </Typography>
        </Box>
    </Box>
);

const SectionHeader = ({ icon, title, color = "#1a1e27" }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 3,
        mt: 1,
        pb: 1,
        borderBottom: '2px solid #f1f5f9'
    }}>
        <Box sx={{
            display: 'flex',
            p: 1,
            borderRadius: '10px',
            bgcolor: color,
            color: '#ffffff',
            boxShadow: `0 8px 16px ${color}30`
        }}>
            <Icon icon={icon} style={{ fontSize: '20px' }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={700} color="#1a1e27">
            {title}
        </Typography>
    </Box>
);

const decryptData = (encryptedData) => {
    try {
        if (!encryptedData || typeof encryptedData !== 'string') return encryptedData;

        // Format check: iv:ciphertext
        if (encryptedData.includes(':')) {
            const parts = encryptedData.split(':');
            if (parts.length !== 2) return encryptedData;

            const iv = CryptoJS.enc.Hex.parse(parts[0]);
            const encrypted = CryptoJS.enc.Hex.parse(parts[1]);

            // standard key for this project family
            const key = CryptoJS.enc.Utf8.parse("12345678901234567890123456789012");

            const decrypted = CryptoJS.AES.decrypt(
                { ciphertext: encrypted },
                key,
                { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
            );

            const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
            return decryptedText || encryptedData;
        }

        // Fallback for simple encryption if ever used
        const key = CryptoJS.enc.Utf8.parse("12345678901234567890123456789012");
        const bytes = CryptoJS.AES.decrypt(encryptedData, key);
        const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
        return decryptedText || encryptedData;
    } catch (error) {
        console.error("Decryption failed:", error);
        return encryptedData;
    }
};

const maskId = (value) => {
    if (!value) return "";

    const visibleDigits = 2; // last 2 digits visible
    const length = value.length;

    if (length <= visibleDigits) return value;

    const maskedPart = "X".repeat(length - visibleDigits);
    const visiblePart = value.slice(-visibleDigits);

    return maskedPart + visiblePart;
};

const UserPage = ({ pageName }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const loggedInrole = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const initialStoreId = adminStoreIds[0] || "";
    const isAddEmployee = location.pathname === '/users/add';
    const isAddFranchiseAdmin = location.pathname === '/franchisor-admin/add';
    const [searchQuery, setSearchQuery] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [IdPreviewUrl, setIdPreviewUrl] = useState(null);
    const [emailError, setEmailError] = useState("");
    const [employeeImage, setEmployeeImage] = useState(null);
    const [IdProofImage, setIdProofImage] = useState(null);
    const [showIdProof, setShowIdProof] = useState(false);
    const [users, setUsers] = useState([]);
    const [stores, setStores] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [open, setOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [limit, setLimit] = useState(5);
    const [isPopupMode, setIsPopupMode] = useState(false);
    const sessionToken = localStorage.getItem("Token");
    const [isEditMode, setIsEditMode] = useState(false);
    const [editEmployeeId, setEditEmployeeId] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordMatchError, setPasswordMatchError] = useState('');
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isAttendanceMode, setIsAttendanceMode] = useState(false);
    const [currentDateTime, setCurrentDateTime] = useState("");
    const [isViewUserPopup, setIsViewUserPopup] = useState(false);
    const [viewUserData, setViewUserData] = useState(null);
    const [selectedStoreId, setSelectedStoreId] = useState(
        initialStoreId
    );
    const [errors, setErrors] = useState({});
    const [sortField, setSortField] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState(-1);
    const [showLoader, setShowLoader] = useState(false);
    const [loaderText, setLoaderText] = useState("");
    const sort = [[sortField, sortOrder]];

    const handleSort = (field) => {
        const newOrder =
            sortField === field ? (sortOrder === 1 ? -1 : 1) : -1;

        setSortField(field);
        setSortOrder(newOrder);
    };
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: "",
        phoneNumber: '',
        gender: '',
        employeeImageObject: '',
        departmentId: '',
        password: '',
        confirmPassword: '',
        staffId: '',
        dateOfBirth: '',
        dateOfJoining: '',
        dateOfResignation: '',
        age: '',
        currentAddress: '',
        permanentAddress: '',
        bloodGroup: '',
        emergencyContactPerson: '',
        emergencyContactNumber: '',
        idProoftype: '',
        idProofImageObject: '',
        refferedByName: '',
        refferedByContactNumber: '',
        storeIds: initialStoreId,
        isActive: true,
    });

    const triggerRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const handleViewUser = async (userId) => {
        try {
            const token = localStorage.getItem("Token");
            const res = await getData(`users/user/${userId}`, {}, token);
            if (res?.data) {
                const data = Array.isArray(res.data) ? res.data[0] : res.data;


                setViewUserData(data);
                setIsViewUserPopup(true);
            }
        } catch (error) {
            console.error("View User Error:", error);
            toast.error("Failed to load user details");
        }
    };

    const validateEmail = (email) => {
        // Basic RFC 5322 official email regex simplified
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleEmailChange = (e) => {
        const email = e.target.value.toLowerCase();
        setFormData((prev) => ({ ...prev, email }));

        if (!validateEmail(email)) {
            setEmailError("Please enter a valid email address");
        } else {
            setEmailError("");
        }
    };

    const validateFields = (data, employeeImage) => {
        let newErrors = {};

        // Roles that DO NOT need Store & Department
        const rolesWithoutStoreDept = [
            "SuperAdmin",
            "SelfAdmin",
            "FranchiseAdmin",
            "Accountant",
            "StoreCreator",
            "Manager"
        ];


        if (!data.name?.trim()) newErrors.name = "Name is required";

        if (!data.role) newErrors.role = "Role is required";

        if (!data.phoneNumber) {
            newErrors.phoneNumber = "Phone number is required";
        } else if (!/^[6-9]\d{9}$/.test(data.phoneNumber)) {
            newErrors.phoneNumber = "Enter a valid phone number";
        }

        if (!data.email) {
            newErrors.email = "Email is required";
        } else if (!/^\S+@\S+\.\S+$/.test(data.email)) {
            newErrors.email = "Invalid email format";
        }

        if (!data.gender) newErrors.gender = "Gender is required";

        // ⭐ Store & Department validation ONLY when required
        if (!rolesWithoutStoreDept.includes(data.role)) {
            // Store required
            if (!data.storeIds || data.storeIds.length === 0) {
                newErrors.storeIds = "Branch name is required";
            }

        }

        // Password validations
        if (!data.password) {
            newErrors.password = "Password is required";
        } else if (data.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }

        if (!data.confirmPassword) {
            newErrors.confirmPassword = "Confirm password is required";
        } else if (data.password !== data.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        return newErrors;
    };


    const handleAddEmployee = async (e) => {
        e.preventDefault();

        const validationErrors = validateFields(formData, employeeImage);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        setShowLoader(true);
        try {
            const token = localStorage.getItem("Token");
            const userId = localStorage.getItem("userId");

            if (employeeImage && userId) {
                const uploadResult = await uploadFile(`files/upload/${userId}`, employeeImage);

                if (uploadResult?.status === 200) {
                    const employeeData = {
                        name: formData.name,
                        email: formData.email,
                        gender: formData.gender,
                        role: formData.role,
                        phoneNumber: formData.phoneNumber,
                        employeeImageObject: uploadResult?.data,
                        idProofImageObject: "", // Will be updated if IdProofImage exists
                        storeIds: formData.storeIds || [],
                        staffId: formData.staffId,
                        password: formData.password,
                        dateOfBirth: formData.dateOfBirth,
                        dateOfJoining: formData.dateOfJoining,
                        dateOfResignation: formData.dateOfResignation,
                        age: formData.age,
                        currentAddress: formData.currentAddress,
                        permanentAddress: formData.permanentAddress,
                        bloodGroup: formData.bloodGroup,
                        emergencyContactPerson: formData.emergencyContactPerson,
                        emergencyContactNumber: formData.emergencyContactNumber,
                        idProoftype: formData.idProoftype,
                        idProofImageObject: "",
                        refferedByName: formData.refferedByName,
                        refferedByContactNumber: formData.refferedByContactNumber,
                        isActive: formData.isActive !== undefined ? formData.isActive : true,
                    };

                    if (IdProofImage) {
                        const idProofUploadResult = await uploadFile(`files/upload/${userId}`, IdProofImage);
                        if (idProofUploadResult?.status === 200 || idProofUploadResult?.success) {
                            employeeData.idProofImageObject = idProofUploadResult.data;
                        } else {
                            toast.error("ID Proof image upload failed");
                            setShowLoader(false);
                            return;
                        }
                    }

                    const response = await postData("users/signup", employeeData, token);

                    if (response?.status === 200 || response?.success) {
                        const toastLabel = pageName === "FranchisorAdmin" ? "Franchise Admin" : "User";
                        toast.success(response.message || `${toastLabel} added successfully`);

                        setFormData({
                            name: "",
                            email: "",
                            phoneNumber: "",
                            role: "",
                            gender: "",
                            employeeImageObject: "",
                            storeIds: loggedInrole === "FranchiseAdmin" ? [] : [...adminStoreIds],
                            departmentId: "",
                            staffId: "",
                            password: "",
                            confirmPassword: "",
                            dateOfBirth: "",
                            dateOfJoining: "",
                            dateOfResignation: "",
                            age: "",
                            currentAddress: "",
                            permanentAddress: "",
                            bloodGroup: "",
                            emergencyContactPerson: "",
                            emergencyContactNumber: "",
                            idProoftype: "",
                            idProofImageObject: "",
                            refferedByName: "",
                            refferedByContactNumber: ""
                        });

                        setEmployeeImage(null);
                        setIdProofImage(null);
                        setPreviewUrl(null);
                        setIdPreviewUrl(null);
                        setErrors({});
                        navigate(isAddFranchiseAdmin ? "/franchisor-admin" : "/users");
                        triggerRefresh();
                    } else {
                        toast.error(response?.message || "Failed to add employee");
                    }
                } else {
                    toast.error(uploadResult?.message || "Image upload failed");
                }
            }
        } catch (error) {
            console.error("Add Employee Error:", error);
            toast.error(error.message || "Add Employee Failed");
        } finally {
            setTimeout(() => {
                setShowLoader(false);
            }, 1000);
        }
    };


    useEffect(() => {
        fetchEmployees(currentPage, limit, searchQuery, selectedStoreId);
    }, [currentPage, limit, refreshKey, searchQuery, selectedStoreId, sortField, sortOrder, pageName]);

    useEffect(() => {
        setFormData({
            ...formData,
            role: "FranchiseAdmin",
        });
    }, [isAddFranchiseAdmin]);

    const fetchEmployees = async (page, limit, searchQuery) => {
        const loginRole = localStorage.getItem("role");
        const params = {
            page,
            limit: limit === 'all' ? 999999 : limit,
            search: searchQuery?.trim() || "",
            role: pageName === "FranchisorAdmin" ? ["FranchiseAdmin"] : ["SelfAdmin", "Accountant", "StoreCreator", "Manager"],
            sort: JSON.stringify([[sortField, sortOrder]])
        }

        if (loginRole !== "SuperAdmin") {
            const sid = Array.isArray(selectedStoreId)
                ? selectedStoreId[0]
                : selectedStoreId;

            if (sid && sid.length === 24) {
                params.storeId = sid;
            }
        }

        try {
            setLoading(true);
            const response = await getData('users/users', params, sessionToken);
            const resultsWrapper = response?.data?.[0];
            const employeeList = resultsWrapper?.paginatedResults || [];
            const totalCount = resultsWrapper?.totalCount || 0;
            const totalPages = resultsWrapper?.totalPages || 1;

            setUsers(employeeList);
            setTotalCount(totalCount);
            setTotalPages(totalPages);
        } catch (error) {
            toast.error(error.message || `Failed to fetch ${pageName === "FranchisorAdmin" ? "franchisor admins" : "users"}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateEmployee = async (e) => {
        e.preventDefault();

        setShowLoader(true);

        try {
            const token = localStorage.getItem("Token");
            const userId = localStorage.getItem("userId");

            let updatedImageObject = formData.employeeImageObject;

            if (employeeImage && userId) {
                const uploadResult = await uploadFile(`files/upload/${userId}`, employeeImage);

                if (uploadResult?.status === 200 || uploadResult?.success) {
                    updatedImageObject = uploadResult.data;
                } else {
                    toast.error(uploadResult?.message || "Employee image upload failed");
                    setShowLoader(false);
                    return;
                }
            }

            let updatedIdProofImageObject = formData.idProofImageObject;
            if (IdProofImage && userId) {
                const uploadResult = await uploadFile(`files/upload/${userId}`, IdProofImage);
                if (uploadResult?.status === 200 || uploadResult?.success) {
                    updatedIdProofImageObject = uploadResult.data;
                } else {
                    toast.error(uploadResult?.message || "ID Proof image upload failed");
                    setShowLoader(false);
                    return;
                }
            }

            const updatedEmployeeData = {
                name: formData.name,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                role: formData.role,
                gender: formData.gender,
                employeeImageObject: updatedImageObject,
                idProofImageObject: updatedIdProofImageObject,
                staffId: formData.staffId,
                ...(formData.password && {
                    password: formData.password,
                    confirmPassword: formData.confirmPassword
                }),
                dateOfBirth: formData.dateOfBirth,
                dateOfJoining: formData.dateOfJoining,
                dateOfResignation: formData.dateOfResignation,
                age: formData.age,
                currentAddress: formData.currentAddress,
                permanentAddress: formData.permanentAddress,
                bloodGroup: formData.bloodGroup,
                emergencyContactPerson: formData.emergencyContactPerson,
                emergencyContactNumber: formData.emergencyContactNumber,
                idProoftype: formData.idProoftype,
                idProofImageObject: updatedIdProofImageObject,
                refferedByName: formData.refferedByName,
                refferedByContactNumber: formData.refferedByContactNumber,
                isActive: formData.isActive,
            };

            const response = await putData(
                `users/user/${editEmployeeId}`,
                updatedEmployeeData,
                token
            );

            const toastLabel = pageName === "FranchisorAdmin" ? "Franchise Admin" : "User";
            toast.success(response.message || `${toastLabel} updated successfully`);

            setFormData({
                name: "",
                email: "",
                phoneNumber: "",
                role: "",
                gender: "",
                employeeImageObject: "",
                storeId: "",
                departmentId: "",
                staffId: "",
                password: "",
                confirmPassword: "",
                dateOfBirth: "",
                dateOfJoining: "",
                dateOfResignation: "",
                age: "",
                currentAddress: "",
                permanentAddress: "",
                bloodGroup: "",
                emergencyContactPerson: "",
                emergencyContactNumber: "",
                idProoftype: "",
                idProofImageObject: "",
                refferedByName: "",
                refferedByContactNumber: ""
            });

            setEmployeeImage(null);
            setIdProofImage(null);
            setPreviewUrl(null);
            setIdPreviewUrl(null);
            setIsPopupMode(false);
            setIsEditMode(false);

            fetchEmployees(currentPage, limit);
            triggerRefresh();

        } catch (error) {
            toast.error("Error updating employee.");
        } finally {
            setTimeout(() => {
                setShowLoader(false);
            }, 1000);
        }
    };


    const handleDeleteEmployee = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('Token');
            const response = await deleteData(`users/user/${employeeToDelete}`, token);
            const toastLabel = pageName === "FranchisorAdmin" ? "Franchise Admin" : "User";
            toast.success(response.message || `${toastLabel} deleted successfully`);
            setEmployeeToDelete(null);
            fetchEmployees(currentPage, limit);
            handleClose();
            triggerRefresh();
        } catch (error) {
            console.error("Delete error:", error);
            toast.error(error.message || "Failed to delete Employee");
        }
    }

    const handleToggleStatus = async (user, newStatus) => {
        try {
            const token = localStorage.getItem('Token');

            // Optimistic Update
            setUsers(prev => prev.map(u => u._id === user._id ? { ...u, isActive: newStatus } : u));

            const response = await putData(
                `users/user/${user._id}`,
                { isActive: newStatus },
                token
            );

            if (response?.status === 200 || response?.success || response?.data) {
                toast.success(response.message || "Status updated successfully");
                triggerRefresh();
            } else {
                setUsers(prev => prev.map(u => u._id === user._id ? { ...u, isActive: user.isActive } : u));
                toast.error(response?.message || "Failed to update status");
            }
        } catch (error) {
            console.error("Status update error:", error);
            setUsers(prev => prev.map(u => u._id === user._id ? { ...u, isActive: user.isActive } : u));
            toast.error(error.message || "Failed to update status");
        }
    };

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const token = localStorage.getItem("Token");
                const data = await getData("stores/stores", {}, token);
                console.log("API Full Response:", data);

                const results = data?.data?.[0]?.paginatedResults || [];
                setStores(results);
            } catch (error) {
                console.error("Failed to load stores", error);
            }
        };
        fetchStores();
    }, []);

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const token = localStorage.getItem("Token");
                const data = await getData('departments/departments', {}, token);
                console.log('API Full Response:', data);
                const results = data?.data?.[0]?.paginatedResults || [];
                setDepartments(results);

            } catch (error) {
                console.error("Failed to load sub categories", error);
            }
        };
        fetchDepartments();
    }, []);

    useEffect(() => {
        let interval;
        if (isAttendanceMode) {
            setCurrentDateTime(dayjs().format("DD/MM/YYYY hh:mm A"));
            interval = setInterval(() => {
                setCurrentDateTime(dayjs().format("DD/MM/YYYY hh:mm A"));
            }, 1000); // update every second
        }
        return () => clearInterval(interval);
    }, [isAttendanceMode]);

    const handleOpen = (employeeId) => {
        setEmployeeToDelete(employeeId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEmployeeToDelete(null);
    };


    const markAttendance = async (employeeId, status, date) => {
        try {
            const token = localStorage.getItem("Token");
            const today = new Date().toISOString().split('T')[0];
            const body = { employeeId, date: today, status };
            const response = await postData("users/user/attendance", body, token);
            toast.success(response.message || "Attendance marked successfully");
            setIsAttendanceMode(true);
        } catch (error) {
            console.error("Error marking attendance:", error);
            toast.error("Failed to mark attendance");
        }
    }

    const commonCellStyle = {
        color: '#525252',
        fontSize: '12px',
        fontWeight: 600,
        textAlign: 'center'
    };

    const headings = [
        'S.NO',
        'Image',
        'Name',
        'Email',
        'Phone Number',
        'Role',
        ...(pageName === "TVFM-Users" ? ['Staff ID'] : []),
        ...(pageName === "FranchisorAdmin" ? ['Store'] : []),
        'Status',
        'Action',
    ];

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "first") return 1;
            if (direction === "last") return totalPages;
            if (direction === "next") return Math.min(prev + 1, totalPages);
            if (direction === "prev") return Math.max(prev - 1, 1);
            return prev;
        });
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        const newLimit = value === "all" ? "all" : Number(value);
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        // For firstName and lastName: only alphabets, no spaces
        if ((name === 'firstName' || name === 'lastName')) {

            if (/[^a-zA-Z]/.test(value)) return;

            const capitalizedValue =
                value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

            setFormData((prev) => ({
                ...prev,
                [name]: capitalizedValue,
            }));
            return;
        }

        if (name === 'confirmPassword' || name === 'password') {
            const newPassword = name === 'password' ? value : formData.password;
            const newConfirm = name === 'confirmPassword' ? value : formData.confirmPassword;

            if (newPassword && newConfirm && newPassword !== newConfirm) {
                setPasswordMatchError('Passwords do not match');
            } else {
                setPasswordMatchError('');
            }
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: '#F7EAE2',
        borderRadius: 12,
        boxShadow: 24,
        p: 4,
        border: 0
    };
    function calculateAge(dob) {
        if (!dob) return "";
        const today = new Date();
        const birthDate = new Date(dob);

        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();

        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    }
    function validateIdProof(idProoftype, idProofNumber) {
        switch (idProoftype) {
            case "Ration Card":
                // usually alphanumeric 10–20 chars
                return /^[A-Z0-9]{10,20}$/i.test(idProofNumber);

            case "Voter ID":
                // 3 letters + 7 digits (e.g., ABC1234567)
                return /^[A-Z]{3}[0-9]{7}$/i.test(idProofNumber);

            case "Aadhaar":
                // exactly 12 digits
                return /^[0-9]{12}$/.test(idProofNumber);

            case "PAN CARD":
                // 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)
                return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(idProofNumber);
            case "Passport":
                // 1 letter + 7 digits (e.g., A1234567)
                return /^[A-PR-WYa-pr-wy][0-9]{7}$/.test(idProofNumber);

            case "Driving License":
                // 2 letters + 2 digits + 11 digits (e.g., TN10XXXXXXXXXXX)
                return /^[A-Z]{2}[0-9]{2}[0-9]{11}$/.test(idProofNumber);

            default:
                return false;
        }
    }

    useEffect(() => {
        const handleStoreChange = () => {
            const storeId = selectedStoreId;
            setSelectedStoreId(storeId);
            setCurrentPage(1);
        };
        setLoading(true);
        window.addEventListener("storeChanged", handleStoreChange);
        return () => window.removeEventListener("storeChanged", handleStoreChange);
    }, []);

    let allowedUserRoles = [];

    if (loggedInrole === "SuperAdmin" || loggedInrole === "SelfAdmin") {
        allowedUserRoles = USER_ROLES.filter(r => r !== loggedInrole);
    } else {
        allowedUserRoles = [];
    }

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toISOString().slice(0, 16);
    };

    const handleDateChange = (fieldName, value) => {
        if (!value) {
            setFormData(prev => ({ ...prev, [fieldName]: value }));
            return;
        }

        // YYYY-MM-DD → extract year
        const year = value.split("-")[0];

        if (year.length > 4) {
            toast.error("Year should contain only 4 digits");
            return; // 🚫 block update
        }

        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    return (
        <div className="product-layout">
            <div className="product-content">
                {showLoader && (
                    <div className="scissor-overlay">
                        <div className="scissor-loader">
                            <TVFMLoader />
                        </div>
                    </div>
                )}
                <main className="product-main">
                    {isAddEmployee ? (
                        <div className="add-product-form">
                            <Typography variant="h6" mb={2}>
                                Add User
                            </Typography>

                            <form className="employee-form" onSubmit={handleAddEmployee}>
                                <div className="employee-form-row">
                                    <div className="employee-form-group-image">
                                        <div className="image-upload-wrapper">
                                            <div className="image-placeholder">
                                                <img
                                                    src={previewUrl || '/assets/mdi--user.svg'}
                                                    alt="Preview"
                                                    className="round-image"
                                                />
                                            </div>
                                        </div>
                                        <div className="upload-icon-container">
                                            <label htmlFor="employeeImage" className="upload-icon-btn">
                                                Upload Image
                                                <span className="required">*</span>
                                                <Icon icon={trayUpload} width="24" height="24" />
                                                <input
                                                    type="file"
                                                    id="employeeImage"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        setEmployeeImage(file);
                                                        setPreviewUrl(URL.createObjectURL(file));
                                                    }}
                                                />

                                            </label>
                                            {errors.employeeImage && <p className="field-error">{errors.employeeImage}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="name" className="input-label">
                                            Name<span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter name"
                                            className="category-input"
                                            value={formData.name}
                                            onChange={e =>
                                                setFormData(prev => ({ ...prev, name: e.target.value }))
                                            }
                                        />
                                        {errors.name && <p className="field-error">{errors.name}</p>}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="role" className="input-label">
                                            Role
                                            <span className="required">*</span>
                                        </label>
                                        <select
                                            className="category-input"
                                            value={formData.role}
                                            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                        >
                                            <option value="">Select Role</option>

                                            {allowedUserRoles.map(role => (
                                                <option key={role} value={role}>{role}</option>
                                            ))}
                                        </select>

                                        {errors.role && <p className="field-error">{errors.role}</p>}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="phoneNumber" className="input-label">
                                            Mobile Number
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="category-input"
                                            placeholder="Enter Mobile Number"
                                            // disabled={!formData.role}
                                            maxLength={10}
                                            title="Mobile Number must be exactly 10 digits"
                                            value={formData.phoneNumber}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                // Allow only digits and max 10 characters
                                                if (/^\d{0,10}$/.test(value)) {
                                                    setFormData((prev) => ({ ...prev, phoneNumber: value }));
                                                }
                                            }}
                                        // onBlur={() => {
                                        //     if (formData.phoneNumber.length !== 10) {
                                        //         alert("Mobile Number must be exactly 10 digits");
                                        //     }
                                        // }}
                                        />
                                        {errors.phoneNumber && <p className="field-error">{errors.phoneNumber}</p>}
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="email" className="input-label">
                                            Email
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="category-input"
                                            placeholder="Enter Email"
                                            // disabled={!formData.phoneNumber}
                                            value={formData.email || ""}
                                            onChange={handleEmailChange}
                                        />
                                        {errors.email && <p className="field-error">{errors.email}</p>}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="gender" className="input-label">
                                            Gender
                                            <span className="required">*</span>
                                        </label>
                                        <select
                                            id="gender"
                                            className="category-input"
                                            placeholder="Select Gender"
                                            // disabled={!formData.email}
                                            value={formData.gender}
                                            onChange={(e) => {
                                                const selectedgendertype = e.target.value;
                                                setFormData((prev) => ({ ...prev, gender: selectedgendertype }))
                                            }}
                                        >
                                            <option value='' >Select Gender</option>
                                            <option value='Male'>Male</option>
                                            <option value='Female'>Female</option>
                                            <option value='Other'>Other</option>
                                        </select>
                                        {errors.gender && <p className="field-error">{errors.gender}</p>}
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="password" className="input-label">
                                            Password
                                            <span className="required">*</span>
                                        </label>
                                        <div className="password-container" style={{ position: 'relative' }}>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="Password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className="password-input"
                                            />
                                            {errors.password && <p className="field-error">{errors.password}</p>}

                                            {/* Eye Icon - Perfectly centered inside the input */}
                                            <div
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="password-show"
                                                onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                role="button"
                                                tabIndex="0"
                                                onKeyPress={(e) => e.key === 'Enter' && setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </div>
                                        </div>
                                        {errors.password && <p className="field-error">{errors.password}</p>}
                                        {/* <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="department-input"
                                            required
                                        />
                                        <span className="signup-show" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </span> */}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="password" className="input-label">
                                            Confirm Password
                                            <span className="required">*</span>
                                        </label>
                                        <div className="password-container" style={{ position: 'relative' }}>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                placeholder="Confirm Password"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                className="password-input"
                                                required
                                            />

                                            {/* Eye Icon - Perfectly centered inside the input */}
                                            <div
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="password-show"
                                                onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                role="button"
                                                tabIndex="0"
                                                onKeyPress={(e) => e.key === 'Enter' && setShowPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </div>
                                        </div>
                                        {errors.confirmPassword && <p className="field-error">{errors.confirmPassword}</p>}
                                        {/* <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            placeholder="Confirm Password"
                                            // disabled={!formData.password}
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="department-input"
                                            required
                                        />
                                        <span className="signup-show" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </span> */}
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="dateOfJoining" className="input-label">
                                            Date of Joining <span className="required">*</span>
                                        </label>

                                        <input
                                            type="date"
                                            id="dateOfJoining"
                                            className="department-input"
                                            value={formData.dateOfJoining || ""}
                                            onChange={(e) => handleDateChange("dateOfJoining", e.target.value)}
                                        />

                                        {errors.dateOfJoining && (
                                            <p className="field-error">{errors.dateOfJoining}</p>
                                        )}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="dateOfBirth" className="input-label">
                                            Date Of Birth
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            name="dateOfBirth"
                                            placeholder="enter Date Of Birth"
                                            type="date"
                                            className="department-input"
                                            value={formData.dateOfBirth || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                const year = value?.split("-")[0] || "";

                                                if (year.length > 4) {
                                                    toast.error("Year should contain only 4 digits");
                                                    return;
                                                }

                                                const age = calculateAge(value);

                                                setFormData(prev => ({
                                                    ...prev,
                                                    dateOfBirth: value,
                                                    age
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="age" className="input-label">
                                            Age
                                        </label>
                                        <input
                                            type="number"
                                            className="department-input"
                                            value={formData.age || ""}
                                            readOnly
                                        />
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="bloodGroup" className="input-label">
                                            Blood Group
                                        </label>
                                        <input
                                            name="bloodGroup"
                                            placeholder="Enter Blood Group"
                                            type="text"
                                            className="department-input"
                                            onChange={e =>
                                                setFormData(prev => ({ ...prev, bloodGroup: e.target.value }))
                                            }
                                            value={formData.bloodGroup}
                                        />
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="idProoftype" className="input-label">
                                            ID Proof Type
                                            <span className="required">*</span>
                                        </label>
                                        <select
                                            id="idProoftype"
                                            className="category-input"
                                            placeholder="Select id Proof type"
                                            // disabled={!formData.email}
                                            value={formData.idProoftype}
                                            onChange={(e) => {
                                                const selectedidProoftypetype = e.target.value;
                                                setFormData((prev) => ({ ...prev, idProoftype: selectedidProoftypetype }))
                                            }}
                                        >
                                            <option value='' >Select id Proof type</option>
                                            <option value='Voter ID'>VOTER ID</option>
                                            <option value='Aadhaar'>AADHAR</option>
                                            <option value='Driving License'>DRIVING LICENSE</option>
                                            <option value='Passport'>PASSPORT</option>
                                        </select>
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="idProofImage" className="input-label">
                                            ID Proof Image <span className="required">*</span>
                                        </label>
                                        <div className="id-proof-upload" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <input
                                                type="file"
                                                id="idProofImage"
                                                accept="image/*"
                                                className="department-input"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setIdProofImage(file);
                                                        setIdPreviewUrl(URL.createObjectURL(file));
                                                    }
                                                }}
                                            />
                                            {IdPreviewUrl && (
                                                <div className="preview-container">
                                                    <img
                                                        src={IdPreviewUrl}
                                                        alt="ID Proof Preview"
                                                        style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => window.open(IdPreviewUrl, '_blank')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {errors.idProofImageObject && <p className="field-error">{errors.idProofImageObject}</p>}
                                    </div>
                                </div>
                                <div className="employee-buttons">
                                    <button className="add-Product-button" type="submit" disabled={emailError !== ""} onClick={isAddEmployee ? handleAddEmployee : handleUpdateEmployee}>
                                        {isEditMode ? "Update User" : "Add User"}
                                    </button>
                                    <button className="cancel-Product-button" onClick={() => navigate('/users')}>
                                        cancel
                                    </button>
                                </div>
                            </form>
                        </div >
                    ) : isAddFranchiseAdmin ? (
                        <div className="add-employee-form">
                            <Typography variant="h6" mb={2}>
                                Add Franchisor Admin
                            </Typography>
                            <form className="employee-form" onSubmit={handleAddEmployee}>
                                <div className="employee-form-row">
                                    <div className="employee-form-group-image">
                                        <div className="image-upload-wrapper">
                                            <div className="image-placeholder">
                                                <img
                                                    src={previewUrl || '/assets/mdi--user.svg'}
                                                    alt="Preview"
                                                    className="round-image"
                                                />
                                            </div>
                                        </div>
                                        <div className="upload-icon-container">
                                            <label htmlFor="employeeImage" className="upload-icon-btn">
                                                Upload Image
                                                <span className="required">*</span>
                                                <Icon icon={trayUpload} width="24" height="24" />
                                                <input
                                                    type="file"
                                                    id="employeeImage"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        setEmployeeImage(file);
                                                        setPreviewUrl(URL.createObjectURL(file));
                                                    }}
                                                />

                                            </label>
                                            {errors.employeeImage && <p className="field-error">{errors.employeeImage}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="name" className="input-label">
                                            Name<span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter name"
                                            className="category-input"
                                            value={formData.name}
                                            onChange={e =>
                                                setFormData(prev => ({ ...prev, name: e.target.value }))
                                            }
                                        />
                                        {errors.name && <p className="field-error">{errors.name}</p>}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="role" className="input-label">
                                            Role
                                            <span className="required">*</span>
                                        </label>
                                        <input type="text" className="category-input" value={formData.role} disabled />
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="phoneNumber" className="input-label">
                                            Mobile Number
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="category-input"
                                            placeholder="Enter Mobile Number"
                                            // disabled={!formData.role}
                                            maxLength={10}
                                            title="Mobile Number must be exactly 10 digits"
                                            value={formData.phoneNumber}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                // Allow only digits and max 10 characters
                                                if (/^\d{0,10}$/.test(value)) {
                                                    setFormData((prev) => ({ ...prev, phoneNumber: value }));
                                                }
                                            }}
                                        // onBlur={() => {
                                        //     if (formData.phoneNumber.length !== 10) {
                                        //         alert("Mobile Number must be exactly 10 digits");
                                        //     }
                                        // }}
                                        />
                                        {errors.phoneNumber && <p className="field-error">{errors.phoneNumber}</p>}
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="email" className="input-label">
                                            Email
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="category-input"
                                            placeholder="Enter Email"
                                            // disabled={!formData.phoneNumber}
                                            value={formData.email || ""}
                                            onChange={handleEmailChange}
                                        />
                                        {errors.email && <p className="field-error">{errors.email}</p>}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="gender" className="input-label">
                                            Gender
                                            <span className="required">*</span>
                                        </label>
                                        <select
                                            id="gender"
                                            className="category-input"
                                            placeholder="Select Gender"
                                            // disabled={!formData.email}
                                            value={formData.gender}
                                            onChange={(e) => {
                                                const selectedgendertype = e.target.value;
                                                setFormData((prev) => ({ ...prev, gender: selectedgendertype }))
                                            }}
                                        >
                                            <option value='' >Select Gender</option>
                                            <option value='Male'>Male</option>
                                            <option value='Female'>Female</option>
                                            <option value='Other'>Other</option>
                                        </select>
                                        {errors.gender && <p className="field-error">{errors.gender}</p>}
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="password" className="input-label">
                                            Password
                                            <span className="required">*</span>
                                        </label>
                                        <div className="password-container" style={{ position: 'relative' }}>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="Password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className="password-input"
                                                required
                                            />

                                            {/* Eye Icon - Perfectly centered inside the input */}
                                            <div
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="password-show"
                                                onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                role="button"
                                                tabIndex="0"
                                                onKeyPress={(e) => e.key === 'Enter' && setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </div>
                                        </div>
                                        {errors.password && <p className="field-error">{errors.password}</p>}
                                        {/* <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="department-input"
                                            required
                                        />
                                        <span className="signup-show" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </span> */}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="password" className="input-label">
                                            Confirm Password
                                            <span className="required">*</span>
                                        </label>
                                        <div className="password-container" style={{ position: 'relative' }}>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                placeholder="Confirm Password"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                className="password-input"
                                                required
                                            />

                                            {/* Eye Icon - Perfectly centered inside the input */}
                                            <div
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="password-show"
                                                onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                role="button"
                                                tabIndex="0"
                                                onKeyPress={(e) => e.key === 'Enter' && setShowPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </div>
                                        </div>
                                        {errors.confirmPassword && <p className="field-error">{errors.confirmPassword}</p>}
                                        {/* <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            placeholder="Confirm Password"
                                            // disabled={!formData.password}
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="department-input"
                                            required
                                        />
                                        <span className="signup-show" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </span> */}
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="dateOfBirth" className="input-label">
                                            Date Of Birth
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            name="dateOfBirth"
                                            placeholder="enter Date Of Birth"
                                            type="date"
                                            className="department-input"
                                            value={formData.dateOfBirth || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                const year = value?.split("-")[0] || "";

                                                if (year.length > 4) {
                                                    toast.error("Year should contain only 4 digits");
                                                    return;
                                                }

                                                const age = calculateAge(value);

                                                setFormData(prev => ({
                                                    ...prev,
                                                    dateOfBirth: value,
                                                    age
                                                }));
                                            }}
                                        />
                                        {errors.dateOfBirth && <p className="field-error">{errors.dateOfBirth}</p>}
                                    </div>


                                    <div className="employee-form-group">
                                        <label htmlFor="age" className="input-label">
                                            Age
                                        </label>
                                        <input
                                            type="number"
                                            className="department-input"
                                            value={formData.age || ""}
                                            readOnly
                                        />
                                    </div>
                                </div>
                                <div className="employee-form-row">
                                    <div className="employee-form-group">
                                        <label htmlFor="bloodGroup" className="input-label">
                                            Blood Group
                                        </label>
                                        <input
                                            name="bloodGroup"
                                            placeholder="Enter Blood Group"
                                            type="text"
                                            className="department-input"
                                            onChange={e =>
                                                setFormData(prev => ({ ...prev, bloodGroup: e.target.value }))
                                            }
                                            value={formData.bloodGroup}
                                        />
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="idProoftype" className="input-label">
                                            ID Proof Type
                                            <span className="required">*</span>
                                        </label>
                                        <select
                                            id="idProoftype"
                                            className="category-input"
                                            placeholder="Select id Proof type"
                                            // disabled={!formData.email}
                                            value={formData.idProoftype}
                                            onChange={(e) => {
                                                const selectedidProoftypetype = e.target.value;
                                                setFormData((prev) => ({ ...prev, idProoftype: selectedidProoftypetype }))
                                            }}
                                        >
                                            <option value='' >Select id Proof type</option>
                                            <option value='Voter ID'>VOTER ID</option>
                                            <option value='Aadhaar'>AADHAR</option>
                                            <option value='Driving License'>DRIVING LICENSE</option>
                                            <option value='Passport'>PASSPORT</option>
                                        </select>
                                        {errors.idProoftype && <p className="field-error">{errors.idProoftype}</p>}
                                    </div>
                                    <div className="employee-form-group">
                                        <label htmlFor="idProofImage" className="input-label">
                                            ID Proof Image <span className="required">*</span>
                                        </label>
                                        <div className="id-proof-upload" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <input
                                                type="file"
                                                id="idProofImage"
                                                accept="image/*"
                                                className="department-input"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setIdProofImage(file);
                                                        setIdPreviewUrl(URL.createObjectURL(file));
                                                    }
                                                }}
                                            />
                                            {IdPreviewUrl && (
                                                <div className="preview-container">
                                                    <img
                                                        src={IdPreviewUrl}
                                                        alt="ID Proof Preview"
                                                        style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => window.open(IdPreviewUrl, '_blank')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {errors.idProofImageObject && <p className="field-error">{errors.idProofImageObject}</p>}
                                    </div>
                                </div>
                                <div className="employee-buttons">
                                    <button className="add-Product-button" type="submit" disabled={emailError !== ""} onClick={isAddEmployee || isAddFranchiseAdmin ? handleAddEmployee : handleUpdateEmployee}>
                                        {isEditMode ? "Update Franchisor Admin" : "Add Franchisor Admin"}
                                    </button>
                                    <button className="cancel-Product-button" onClick={() => navigate(isAddFranchiseAdmin ? "/franchisor-admin" : "/users")}>
                                        cancel
                                    </button>
                                </div>
                            </form>
                        </div >
                    ) :

                        (
                            <>
                                <Typography variant="h6" mb={2}>
                                    {pageName === "FranchisorAdmin" ? "Franchisor Admin" : "TVFM Users"}
                                </Typography>
                                <div className="product-widgets">
                                    <div className="search-input-wrapper">
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            className="user-search-input"
                                            name="search"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <Icon icon="mdi:magnify" className="search-icon" />
                                    </div>
                                    <div className="employee-actions">
                                        <button className="add-Product-button" onClick={() => navigate(
                                            pageName === 'FranchisorAdmin' ? '/franchisor-admin/add' : '/users/add'
                                        )}>
                                            {pageName === 'FranchisorAdmin' ? '+ Add Franchisor Admin' : '+ Add User'}
                                        </button>
                                    </div>
                                </div>
                                <Box component={Paper}>
                                    <TableContainer sx={{
                                        maxHeight: 400,
                                        overflowY: "auto",
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                    }}>
                                        <Table className="custom-employee-table"
                                            sx={{
                                                '& td, & th': {
                                                    border: 'none',
                                                },
                                                border: 'none',
                                            }}>
                                            <TableHead className="custom-product-head">
                                                <TableRow>
                                                    {headings.map((heading, index) => {
                                                        const isName = heading === "Name";
                                                        const isEmail = heading === "Email";
                                                        const isPhoneNumber = heading === "Phone Number";
                                                        const isRole = heading === "Role"
                                                        const sortKey = isName
                                                            ? "name"
                                                            : isEmail
                                                                ? "email"
                                                                : isPhoneNumber
                                                                    ? "phoneNumber"
                                                                    : isRole
                                                                        ? "role"
                                                                        : null

                                                        return (
                                                            <TableCell
                                                                key={index}
                                                                sx={{
                                                                    border: "none",
                                                                    padding: "12px 16px",
                                                                    textAlign: "center",
                                                                    position: "sticky",
                                                                    top: 0,
                                                                    zIndex: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        gap: "4px",
                                                                        color: "#ffffffff",
                                                                        fontSize: "14px",
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {heading}

                                                                    {sortKey && (
                                                                        <span
                                                                            onClick={() => handleSort(sortKey)}
                                                                            style={{
                                                                                fontSize: "10px",
                                                                                color: "#ffffffff",
                                                                                cursor: "pointer",
                                                                                marginLeft: "4px",
                                                                                userSelect: "none",
                                                                            }}
                                                                        >
                                                                            {sortField === sortKey
                                                                                ? sortOrder === 1
                                                                                    ? "▲"
                                                                                    : "▼"
                                                                                : "▲▼"}
                                                                        </span>
                                                                    )}
                                                                </Typography>
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {users.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5}>
                                                            <Typography sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontWeight: 500 }}>
                                                                No Users found.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    users.map((user, index) => (
                                                        <TableRow key={user._id}>
                                                            <TableCell>
                                                                <Typography sx={commonCellStyle}>
                                                                    {(currentPage - 1) * (limit === 'all' ? 0 : limit) + index + 1}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <img
                                                                        src={
                                                                            user.employeeImageObject?.uri
                                                                                ? `${process.env.REACT_APP_API_URL}${user.employeeImageObject.uri}`
                                                                                : "/assets/management-consulting.png"
                                                                        }
                                                                        alt={user.name}
                                                                        style={{
                                                                            width: "40px",
                                                                            height: "40px",
                                                                            borderRadius: "50%",
                                                                            objectFit: "cover",
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell><Typography sx={commonCellStyle}>{user.name || user.firstName}</Typography></TableCell>
                                                            <TableCell><Typography sx={commonCellStyle}>{user.email}</Typography></TableCell>
                                                            <TableCell><Typography sx={commonCellStyle}>{user.phoneNumber}</Typography></TableCell>
                                                            <TableCell><Typography sx={commonCellStyle}>{user.role}</Typography></TableCell>
                                                            {pageName === "TVFM-Users" && (
                                                                <TableCell>
                                                                    <Typography sx={commonCellStyle}>{user.staffId}</Typography>
                                                                </TableCell>
                                                            )}
                                                            {pageName === "FranchisorAdmin" && (
                                                                <TableCell>
                                                                    <Typography sx={commonCellStyle}>
                                                                        {user?.franchiseStores?.length
                                                                            ? user.franchiseStores.map(s => s.storeName).join(", ")
                                                                            : "No Stores"}
                                                                    </Typography>
                                                                </TableCell>
                                                            )}
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <Switch
                                                                        checked={user.isActive !== false}
                                                                        onChange={(e) => handleToggleStatus(user, e.target.checked)}
                                                                        sx={{
                                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                                color: '#3C0008',
                                                                            },
                                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                                backgroundColor: '#3C0008',
                                                                            },
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                {isAttendanceMode ? (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                        <button
                                                                            style={{
                                                                                backgroundColor: '#ffffff',
                                                                                color: '#4CAF50',
                                                                                border: '1px solid #4CAF50',
                                                                                borderRadius: '18px',
                                                                                padding: '6px 12px',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => markAttendance(user._id, "Present")}
                                                                        >
                                                                            Present
                                                                        </button>
                                                                        <button
                                                                            style={{
                                                                                backgroundColor: '#ffffff',
                                                                                color: '#DC3545',
                                                                                border: '1px solid #DC3545',
                                                                                borderRadius: '18px',
                                                                                padding: '6px 12px',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => markAttendance(user._id, "Absent")}
                                                                        >
                                                                            X
                                                                        </button>
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                                                                        <Tooltip title={pageName === "FranchisorAdmin" ? "View Franchise Admin" : "View User"}>
                                                                            <Icon
                                                                                icon={plusIcon}
                                                                                width={20}
                                                                                height={20}
                                                                                style={{ cursor: "pointer", color: "#1a1e27" }}
                                                                                onClick={() => handleViewUser(user._id)}
                                                                            />
                                                                        </Tooltip>
                                                                        {/* <Icon icon={plusIcon} width={20} height={20} /> */}
                                                                        <Tooltip title={pageName === "FranchisorAdmin" ? "Edit Franchise Admin" : "Edit User"}>
                                                                            <Icon icon={editIcon} width={20} height={20} style={{ cursor: "pointer" }} onClick={() => {
                                                                                const formatDateOnly = (dateStr) => {
                                                                                    if (!dateStr) return "";
                                                                                    return new Date(dateStr).toISOString().slice(0, 10);
                                                                                };
                                                                                setFormData({
                                                                                    name: user.name || '',
                                                                                    email: user.email || '',
                                                                                    role: user.role || '',
                                                                                    phoneNumber: user.phoneNumber || '',
                                                                                    gender: user.gender || '',
                                                                                    employeeImageObject: user.employeeImageObject || '',
                                                                                    departmentId: user.departmentId || '',
                                                                                    staffId: user.staffId || '',
                                                                                    dateOfBirth: formatDateOnly(user.dateOfBirth),
                                                                                    dateOfJoining: formatDateOnly(user.dateOfJoining),
                                                                                    dateOfResignation: formatDateOnly(user.dateOfResignation),
                                                                                    age: user.age || '',
                                                                                    currentAddress: user.currentAddress || '',
                                                                                    permanentAddress: user.permanentAddress || '',
                                                                                    bloodGroup: user.bloodGroup || '',
                                                                                    emergencyContactPerson: user.emergencyContactPerson || '',
                                                                                    emergencyContactNumber: user.emergencyContactNumber || '',
                                                                                    idProoftype: user.idProoftype || '',
                                                                                    idProofImageObject: user.idProofImageObject || '',
                                                                                    refferedByName: user.refferedByName || '',
                                                                                    refferedByContactNumber: user.refferedByContactNumber || '',
                                                                                    isActive: user.isActive !== undefined ? user.isActive : true,

                                                                                });
                                                                                setPreviewUrl(
                                                                                    user?.employeeImageObject?.uri
                                                                                        ? `${process.env.REACT_APP_API_URL}${user.employeeImageObject.uri}`
                                                                                        : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
                                                                                );
                                                                                setIdPreviewUrl(
                                                                                    user?.idProofImageObject?.uri
                                                                                        ? `${process.env.REACT_APP_API_URL}${user.idProofImageObject.uri}`
                                                                                        : ""
                                                                                );
                                                                                setEmployeeImage(null);
                                                                                setIdProofImage(null);
                                                                                setEditEmployeeId(user._id);
                                                                                setIsPopupMode(true);
                                                                                setIsEditMode(true);

                                                                            }} />
                                                                        </Tooltip>
                                                                        {loggedInrole !== "SelfAdmin" &&
                                                                            loggedInrole !== user.role &&
                                                                            user.isActive !== false && (
                                                                                <Tooltip title={pageName === "FranchisorAdmin" ? "Delete Franchise Admin" : "Delete User"}>
                                                                                    <Icon
                                                                                        icon={deleteIcon}
                                                                                        width={20}
                                                                                        height={20}
                                                                                        style={{ color: '#C10000', cursor: 'pointer' }}
                                                                                        onClick={() => handleOpen(user._id)}
                                                                                    />
                                                                                </Tooltip>
                                                                            )}

                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>

                                                    ))
                                                )}

                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <div className="product-bottom">
                                        <div className="product-section show-entries">
                                            <span className="text-label">Show</span>
                                            <select className="entries-select" value={limit}
                                                onChange={handleItemsPerPageChange}
                                            >
                                                <option value="5">5</option>
                                                <option value="10">10</option>
                                                <option value="25">25</option>
                                                <option value="all">All</option>
                                            </select>
                                            <span className="text-label">Entries</span>
                                        </div>
                                        <div className="product-section showing-range">
                                            {
                                                limit === 'all'
                                                    ? `Showing all ${totalCount} entries`
                                                    : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                                            }
                                        </div>
                                        <div className="product-section spacer"></div>
                                        <div className="product-section pagination-buttons">
                                            <button
                                                className="pagination-btn"
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange("first")}
                                            >
                                                <Icon icon="mdi:chevron-double-left" />
                                            </button>
                                            <button
                                                className="pagination-btn"
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange("prev")}
                                            >
                                                <Icon icon="mdi:chevron-left" />
                                            </button>
                                            <button
                                                className="pagination-btn"
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange("next")}
                                            >
                                                <Icon icon="mdi:chevron-right" />
                                            </button>
                                            <button
                                                className="pagination-btn"
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange("last")}
                                            >
                                                <Icon icon="mdi:chevron-double-right" />
                                            </button>
                                        </div>
                                    </div>
                                </Box>
                                {isPopupMode && (
                                    <div className="popup-overlay">
                                        <div className="user-popup-wrapper">
                                            <Icon
                                                icon={closeIcon}
                                                className="popup-close-icon-outside"
                                                onClick={() => {
                                                    setIsPopupMode(false);
                                                    setIsEditMode(false);
                                                    setFormData({
                                                        name: '',
                                                        email: '',
                                                        role: '',
                                                        phoneNumber: '',
                                                        employeeImageObject: null,
                                                        password: '',
                                                        age: '',
                                                        dateOfBirth: '',
                                                        dateOfJoining: '',
                                                        dateOfResignation: '',
                                                        bloodGroup: '',
                                                        emergencyContactPerson: '',
                                                        emergencyContactNumber: '',
                                                        idProoftype: '',
                                                        idProofImageObject: '',
                                                        refferedByName: '',
                                                        refferedByContactNumber: ''
                                                    });
                                                    setPreviewUrl(null);
                                                    setLoaderText('');
                                                }}
                                            />

                                            <div className="popup-form-Employee">
                                                <Typography variant="h6" textAlign="center" mb={4}>
                                                    {pageName === "FranchisorAdmin" ? (isEditMode ? "Edit Franchisor Admin" : "Add Franchisor Admin") : (isEditMode ? "Edit User" : "Add User")}
                                                </Typography>
                                                <form className="popup-form-body" onSubmit={handleUpdateEmployee}>
                                                    <div className="form-group-row">
                                                        <div className="employee-form-group-image">
                                                            <div className="image-upload-wrapper">
                                                                <div className="image-placeholder">
                                                                    <img
                                                                        src={previewUrl || '/assets/mdi--user.svg'}
                                                                        alt="Preview"
                                                                        className="round-image"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="upload-icon-container">
                                                                <label htmlFor="employeeImage" className="upload-icon-btn">
                                                                    Upload Image
                                                                    <Icon icon={trayUpload} width="24" height="24" />
                                                                    <input
                                                                        type="file"
                                                                        id="employeeImage"
                                                                        accept="image/*"
                                                                        style={{ display: 'none' }}
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0];
                                                                            setEmployeeImage(file);
                                                                            setPreviewUrl(URL.createObjectURL(file));
                                                                        }}
                                                                    />

                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="form-group-row">
                                                        <div className="form-group">
                                                            <label htmlFor="name" className="input-label">
                                                                Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                placeholder="Enter employee name"
                                                                className="category-input"
                                                                value={formData.name}
                                                                onChange={e =>
                                                                    setFormData(prev => ({ ...prev, name: e.target.value }))
                                                                }
                                                            />
                                                        </div>
                                                        {
                                                            isAddEmployee ? (<div className="form-group">
                                                                <label htmlFor="role" className="input-label">
                                                                    Role
                                                                    <span className="required">*</span>
                                                                </label>
                                                                <select
                                                                    id="role"
                                                                    className="category-input"
                                                                    required
                                                                    // disabled={!formData.name}
                                                                    value={formData.role}
                                                                    onChange={(e) => {
                                                                        const selectedRole = e.target.value;
                                                                        setFormData((prev) => ({ ...prev, role: selectedRole }))
                                                                    }}
                                                                >
                                                                    <option value="" disabled>Select Role</option>
                                                                    {allowedUserRoles.map((role) => (
                                                                        <option key={role} value={role}>
                                                                            {role}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {errors.role && <p className="field-error">{errors.role}</p>}
                                                            </div>) : (
                                                                <div className="form-group">
                                                                    <label htmlFor="role" className="input-label">
                                                                        Role
                                                                        <span className="required">*</span>
                                                                    </label>
                                                                    <input
                                                                        id="role"
                                                                        className="category-input"
                                                                        required
                                                                        disabled
                                                                        value={formData.role}
                                                                    />
                                                                </div>
                                                            )
                                                        }

                                                        <div className="form-group">
                                                            <label htmlFor="phoneNumber" className="input-label">Mobile Number</label>
                                                            <input
                                                                type="text"
                                                                className="category-input"
                                                                placeholder="Enter Mobile Number"
                                                                // disabled={!formData.role}
                                                                maxLength={12}
                                                                title="Mobile Number must be exactly 12 digits"
                                                                value={formData.phoneNumber}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    // Allow only digits and max 12 characters
                                                                    if (/^\d{0,10}$/.test(value)) {
                                                                        setFormData((prev) => ({ ...prev, phoneNumber: value }));
                                                                    }
                                                                }}
                                                            // onBlur={() => {
                                                            //     if (formData.phoneNumber.length !== 10) {
                                                            //         alert("Mobile Number must be exactly 10 digits");
                                                            //     }
                                                            // }}
                                                            />
                                                        </div>

                                                    </div>
                                                    <div className="form-group-row">

                                                        <div className="form-group">
                                                            <label htmlFor="email" className="input-label">
                                                                Email
                                                            </label>
                                                            <input
                                                                type="email"
                                                                className="category-input"
                                                                placeholder="Enter Email"
                                                                // disabled={!formData.phoneNumber}
                                                                value={formData.email || ""}
                                                                onChange={handleEmailChange}
                                                            />
                                                            {emailError && <p style={{ color: "red" }}>{emailError}</p>}
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="gender" className="input-label">
                                                                Gender
                                                            </label>
                                                            <select
                                                                id="gender"
                                                                className="category-input"
                                                                placeholder="Select Gender"

                                                                value={formData.gender}
                                                                onChange={(e) => {
                                                                    const selectedgendertype = e.target.value;
                                                                    setFormData((prev) => ({ ...prev, gender: selectedgendertype }))
                                                                }}
                                                            >
                                                                <option value='' >Select Gender</option>
                                                                <option value='Male'>Male</option>
                                                                <option value='Female'>Female</option>
                                                                <option value='Other'>Other</option>
                                                            </select>
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="StaffId" className="input-label">
                                                                Staff ID
                                                            </label>
                                                            <input
                                                                type="text"
                                                                name="staffId"
                                                                placeholder="Staff ID"
                                                                onChange={e =>
                                                                    setFormData(prev => ({ ...prev, staffId: e.target.value }))
                                                                }
                                                                value={formData.staffId}
                                                                className="department-input"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-group-row">
                                                        <div className="form-group">
                                                            <label htmlFor="password" className="input-label">
                                                                Password
                                                            </label>
                                                            <input
                                                                type={showPassword ? "text" : "password"}
                                                                name="password"
                                                                placeholder="Password"
                                                                value={formData.password}
                                                                onChange={handleChange}
                                                                className="department-input"

                                                            />
                                                            <span className="user-signup-show" onClick={() => setShowPassword(!showPassword)}>
                                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                            </span>
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="password" className="input-label">
                                                                Confirm Password
                                                            </label>
                                                            <input
                                                                type={showConfirmPassword ? "text" : "password"}
                                                                name="confirmPassword"
                                                                placeholder="Confirm Password"

                                                                value={formData.confirmPassword}
                                                                onChange={handleChange}
                                                                className="department-input"

                                                            />
                                                            <span className="user-signup-show" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                            </span>

                                                            {passwordMatchError && <p className="password-error-text">{passwordMatchError}</p>}
                                                        </div>
                                                    </div>
                                                    <div className="form-group-row">
                                                        <div className="form-group">
                                                            <label htmlFor="dateOfJoining" className="input-label">
                                                                Date of Joining <span className="required">*</span>
                                                            </label>

                                                            <input
                                                                type="date"
                                                                id="dateOfJoining"
                                                                className="department-input"
                                                                value={formData.dateOfJoining || ""}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;

                                                                    const year = value?.split("-")[0] || "";

                                                                    if (year.length > 4) {
                                                                        toast.error("Year should contain only 4 digits");
                                                                        return;
                                                                    }

                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        dateOfJoining: value,
                                                                    }));
                                                                }}
                                                            />

                                                            {errors.dateOfJoining && (
                                                                <p className="field-error">{errors.dateOfJoining}</p>
                                                            )}
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="dateOfBirth" className="input-label">Date-Of-Birth</label>
                                                            <input
                                                                name="dateOfBirth"
                                                                type="date"
                                                                className="department-input"
                                                                value={formData.dateOfBirth || ""}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;

                                                                    const year = value?.split("-")[0] || "";

                                                                    if (year.length > 4) {
                                                                        toast.error("Year should contain only 4 digits");
                                                                        return;
                                                                    }

                                                                    const age = calculateAge(value);

                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        dateOfBirth: value,
                                                                        age
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="age" className="input-label">
                                                                Age
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="department-input"
                                                                value={formData.age || ""}
                                                                readOnly
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-group-row">
                                                        <div className="form-group">
                                                            <label htmlFor="bloodGroup" className="input-label">
                                                                Blood Group
                                                            </label>
                                                            <input
                                                                name="bloodGroup"
                                                                placeholder="Enter Blood Group"
                                                                type="text"
                                                                className="department-input"
                                                                onChange={e =>
                                                                    setFormData(prev => ({ ...prev, bloodGroup: e.target.value }))
                                                                }
                                                                value={formData.bloodGroup}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="idProoftype" className="input-label">
                                                                ID Proof Type
                                                            </label>
                                                            <select
                                                                id="idProoftype"
                                                                className="category-input"
                                                                placeholder="Select id Proof type"
                                                                // disabled={!formData.email}
                                                                value={formData.idProoftype}
                                                                onChange={(e) => {
                                                                    const selectedidProoftypetype = e.target.value;
                                                                    setFormData((prev) => ({ ...prev, idProoftype: selectedidProoftypetype }))
                                                                }}
                                                            >
                                                                <option value='' >Select id Proof type</option>
                                                                <option value='Voter ID'>Voter ID</option>
                                                                <option value='Aadhaar'>Aadhaar</option>
                                                                <option value='Passport'>PASSPORT</option>
                                                                <option value='Driving License'>DRIVING LICENSE</option>
                                                            </select>
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="idProofImage" className="input-label">
                                                                ID Proof Image
                                                            </label>
                                                            <div className="id-proof-upload" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                                <input
                                                                    type="file"
                                                                    id="idProofImage"
                                                                    accept="image/*"
                                                                    className="category-input"
                                                                    onChange={(e) => {
                                                                        const file = e.target.files[0];
                                                                        if (file) {
                                                                            setIdProofImage(file);
                                                                            setIdPreviewUrl(URL.createObjectURL(file));
                                                                        }
                                                                    }}
                                                                />
                                                                {IdPreviewUrl && (
                                                                    <div className="preview-container">
                                                                        <img
                                                                            src={IdPreviewUrl}
                                                                            alt="ID Proof Preview"
                                                                            style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover', cursor: 'pointer' }}
                                                                            onClick={() => window.open(IdPreviewUrl, '_blank')}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="form-group">
                                                            <label htmlFor="isActive" className="input-label">
                                                                Status
                                                            </label>
                                                            <select
                                                                id="isActive"
                                                                className="category-input"
                                                                value={formData.isActive ? "true" : "false"}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({ ...prev, isActive: e.target.value === "true" }))
                                                                }
                                                            >
                                                                <option value="true">Active</option>
                                                                <option value="false">Inactive</option>
                                                            </select>
                                                        </div>


                                                    </div>
                                                    <div className="form-actions">
                                                        <button type="submit" className="add-Product-button">
                                                            {pageName === "FranchisorAdmin" ? (isEditMode ? "Update Franchisor Admin" : "Add Franchisor Admin") : (isEditMode ? "Update User" : "Add User")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="cancel-Product-button"
                                                            onClick={() => setIsPopupMode(false)}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {limit > 5 && users.length > 5 && (
                                    <div className="scroll-down-hint">
                                        <Typography sx={{ display: 'flex', justifyContent: 'center', marginTop: 2, fontSize: 14, fontWeight: 600 }}>
                                            Scroll down to see more Users
                                        </Typography>
                                    </div>
                                )}
                                <Modal
                                    open={open}
                                    onClose={handleClose}
                                    aria-labelledby="modal-modal-title"
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            width: 380,
                                            background: "linear-gradient(135deg, #ffffff, #faf5f6)",
                                            borderRadius: "20px",
                                            boxShadow: "0 25px 70px rgba(0,0,0,0.35)",
                                            p: 4,
                                            outline: "none",
                                            textAlign: "center",
                                            overflow: "hidden"
                                        }}
                                    >
                                        {/* ✅ TOP ICON BADGE */}
                                        <Box
                                            sx={{
                                                width: 74,
                                                height: 74,
                                                borderRadius: "50%",
                                                background: "#3C0008",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                margin: "0 auto 16px auto",
                                                color: "#fff",
                                                fontSize: 30,

                                                boxShadow: "0 12px 28px rgba(26,30,39,0.55)",
                                                animation: "deletePulse 1.6s infinite ease-in-out",

                                                "@keyframes deletePulse": {
                                                    "0%": {
                                                        transform: "scale(1)",
                                                        boxShadow: "0 0 0 0 rgba(26,30,39,0.7)",
                                                    },
                                                    "70%": {
                                                        transform: "scale(1.1)",
                                                        boxShadow: "0 0 0 18px rgba(26,30,39,0)",
                                                    },
                                                    "100%": {
                                                        transform: "scale(1)",
                                                        boxShadow: "0 0 0 0 rgba(26,30,39,0)",
                                                    },
                                                },
                                            }}
                                        >
                                            🗑️
                                        </Box>


                                        {/* ✅ TITLE */}
                                        <Typography
                                            id="modal-modal-title"
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: "#2b2b2b",
                                                letterSpacing: "0.3px"
                                            }}
                                        >
                                            Confirm Delete
                                        </Typography>

                                        {/* ✅ DESCRIPTION */}
                                        <Typography
                                            id="modal-modal-description"
                                            sx={{
                                                mt: 1.2,
                                                fontSize: 14,
                                                color: "#666",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            Are you sure you want to delete this {pageName === "FranchisorAdmin" ? "franchisor admin" : "user"}?
                                        </Typography>

                                        {/* ✅ BUTTON GROUP */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                gap: 2.5,
                                                mt: 3.5
                                            }}
                                        >
                                            {/* ✅ DELETE BUTTON */}
                                            <Button
                                                variant="contained"
                                                onClick={handleDeleteEmployee}
                                                className="add-Product-button"
                                            >
                                                Yes, Delete
                                            </Button>

                                            {/* ✅ CANCEL BUTTON */}
                                            <Button
                                                variant="outlined"
                                                onClick={handleClose}
                                                className="cancel-Product-button"
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                </Modal>
                                {isViewUserPopup && viewUserData && (
                                    <Modal
                                        open={isViewUserPopup}
                                        onClose={() => setIsViewUserPopup(false)}
                                        closeAfterTransition
                                    >
                                        <Box sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: { xs: '95%', sm: '80%', md: 750 },
                                            maxHeight: '85vh',
                                            bgcolor: '#ffffff',
                                            borderRadius: '24px',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            outline: 'none',
                                            animation: 'modalSlideUp 0.4s ease-out',
                                            '@keyframes modalSlideUp': {
                                                '0%': { transform: 'translate(-50%, -40%)', opacity: 0 },
                                                '100%': { transform: 'translate(-50%, -50%)', opacity: 1 }
                                            }
                                        }}>
                                            {/* Top Banner & Profile Header */}
                                            <Box sx={{
                                                height: '140px',
                                                background: '#3C0008',
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                px: 4,
                                                pb: 2
                                            }}>
                                                <IconButton
                                                    onClick={() => setIsViewUserPopup(false)}
                                                    sx={{ position: 'absolute', top: 16, right: 16, color: '#ffffff', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}
                                                >
                                                    <Icon icon={closeIcon} />
                                                </IconButton>

                                                <Box sx={{
                                                    position: 'absolute',
                                                    bottom: '-50px',
                                                    left: '40px',
                                                    width: '120px',
                                                    height: '120px',
                                                    borderRadius: '24px',
                                                    border: '4px solid #ffffff',
                                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                                    overflow: 'hidden',
                                                    bgcolor: '#f8f9fa'
                                                }}>
                                                    <img
                                                        src={viewUserData.employeeImageObject?.uri
                                                            ? `${process.env.REACT_APP_API_URL}${viewUserData.employeeImageObject.uri}`
                                                            : "/assets/management-consulting.png"
                                                        }
                                                        alt={viewUserData.name}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </Box>

                                                <Box sx={{ ml: '140px', mb: 1 }}>
                                                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                                                        {viewUserData.name || viewUserData.firstName}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Chip
                                                            label={viewUserData.role}
                                                            size="small"
                                                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, fontSize: '12px' }}
                                                        />
                                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)', gap: 0.5 }}>
                                                            <Icon icon="mdi:circle" style={{ fontSize: '8px', color: viewUserData.isActive ? '#4CAF50' : '#DC3545' }} />
                                                            <Typography variant="caption" fontWeight={600}>
                                                                {viewUserData.isActive ? 'Active' : 'Inactive'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Modal Content */}
                                            <Box sx={{ mt: '60px', p: 4, overflowY: 'auto', flex: 1, bgcolor: '#f8fafc' }}>

                                                {/* Grid Layout Sections */}
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>

                                                    {/* Section 1: Basic Information */}
                                                    <Box>
                                                        <SectionHeader icon="mdi:account-details" title="Personal Profile" color="#3C0008" />
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, bgcolor: '#ffffff', p: 2, borderRadius: '16px' }}>
                                                            <DetailItem label="Staff ID" value={viewUserData.staffId} icon="mdi:card-account-details-outline" fullWidth color="#3C0008" />
                                                            <DetailItem label="Gender" value={viewUserData.gender} icon="mdi:gender-male-female" color="#3C0008" />
                                                            <DetailItem label="Age" value={viewUserData.age} icon="mdi:calendar-account" color="#3C0008" />
                                                            <DetailItem label="Blood Group" value={viewUserData.bloodGroup} icon="mdi:water" color="#3C0008" />
                                                            <DetailItem label="DOB" value={viewUserData.dateOfBirth ? dayjs(viewUserData.dateOfBirth).format("DD/MM/YYYY") : "-"} icon="mdi:cake-variant" color="#3C0008" />
                                                            <DetailItem label="ID Type" value={viewUserData.idProoftype || "-"} icon="mdi:card-text-outline" color="#3C0008" />
                                                            <DetailItem
                                                                label="ID Proof"
                                                                value={
                                                                    viewUserData.idProofImageObject?.uri ? (
                                                                        <Box sx={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', cursor: 'pointer' }} onClick={() => window.open(`${process.env.REACT_APP_API_URL}${viewUserData.idProofImageObject.uri}`, '_blank')}>
                                                                            <img
                                                                                src={`${process.env.REACT_APP_API_URL}${viewUserData.idProofImageObject.uri}`}
                                                                                alt="ID Proof"
                                                                                style={{ width: '100%', height: '40px', objectFit: 'cover' }}
                                                                            />
                                                                        </Box>
                                                                    ) : "No ID Uploaded"
                                                                }
                                                                icon="mdi:file-image-outline"
                                                                color="#3C0008"
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Section 2: Contact & Emergency */}
                                                    <Box>
                                                        <SectionHeader icon="mdi:contact-phone" title="Contact Info" color="#6d000e" />
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, bgcolor: '#ffffff', p: 2, borderRadius: '16px' }}>
                                                            <DetailItem label="Email Address" value={viewUserData.email} icon="mdi:email-outline" color="#6d000e" />
                                                            <DetailItem label="Phone Number" value={viewUserData.phoneNumber} icon="mdi:phone-outline" color="#6d000e" />
                                                            <DetailItem label="Emergency Contact" value={viewUserData.emergencyContactPerson} icon="mdi:account-alert-outline" color="#6d000e" />
                                                            <DetailItem label="Emergency Phone" value={viewUserData.emergencyContactNumber} icon="mdi:phone-alert-outline" color="#6d000e" />
                                                        </Box>
                                                    </Box>

                                                </Box>
                                            </Box>

                                            {/* Actions Footer */}
                                            <Box sx={{ px: 4, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', bgcolor: '#f8fafc' }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => setIsViewUserPopup(false)}
                                                    sx={{
                                                        background: '#3C0008',
                                                        borderRadius: '12px',
                                                        px: 4,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '14px',
                                                        '&:hover': { background: '#3C0008', boxShadow: '0 4px 12px rgba(26,30,39,0.2)' }
                                                    }}
                                                >
                                                    Dismiss
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Modal>
                                )}
                            </>
                        )}
                </main >
                <Footer />
            </div >

        </div >
    );
};


export default UserPage;